<template>
  <div v-if="showPanel" class="intro-panel" :class="{ expanded: isExpanded }">
    <!-- 展开状态内容 -->
    <div v-if="isExpanded" class="expanded-content">
      <!-- 关闭按钮 -->
      <button class="intro-close-button" @click="closePanel">×</button>
      
      <Transition name="fade" mode="out-in">
        <div v-if="showContent" class="intro-content">
          <div class="intro-text">
            <p class="intro-title">Spatiotemporal Dynamics and Scenario-Based Assessment of Urban Blue-Green Space Accessibility, Equality, and Public Wellbeing in China</p>
            
            <h4 class="intro-section-title">Introduction:</h4>
            <p class="title-content">This data portal offers an integrated platform for exploring spatiotemporal patterns and future projections of urban green-blue space (UBGS) accessibility, equality, and public wellbeing across 279 Chinese cities. Spanning historical (2000–2020) and long-term future (2030–2100) periods, it features interactive visualizations of key metrics, including accessibility to UBGS, distributional equality of UBGS, and associated public wellbeing outcomes.</p>
            <p class="title-content">Leveraging five Shared Socioeconomic Pathway (SSP) scenarios, the platform further enables analysis of potential drivers that shape improvements in accessibility and equality to fulfill wellbeing. By synthesizing multi-dimensional data with user-friendly tools, this resource supports evidence-based research, policy design, and cross-disciplinary dialogue on sustainable urban development.</p>
            
            <p class="title-content">The UBGS accessibility refers to the UBGS that residents can obtain within half an hour while walking (unit: square meters per person). The UBGS equality refers to the disparity in the access of urban residents to UBGS.</p>
            
            <p class="title-content">This data-driven platform is supported by the National Natural Science Foundation of China (Grant No. 72474036) and the Nature-based Solutions for Urban Resilience in the Anthropocene (NATURA) project.</p>
          </div>
        </div>
      </Transition>
    </div>
    
    <!-- 收缩状态内容 -->
    <div v-else class="collapsed-content" @click="expandPanel">
      <div class="expand-icon">ⓘ</div>
      <div class="expand-text">Info</div>
    </div>
  </div>
</template>

<script>
import { ref, Transition } from 'vue';

export default {
  name: 'IntroPanel',
  components: {
    Transition
  },
  setup() {
    const showPanel = ref(true);
    
    // 从localStorage读取状态，默认为展开
    const savedState = localStorage.getItem('introPanelExpanded');
    const isExpanded = ref(savedState !== null ? JSON.parse(savedState) : true);
    const showContent = ref(isExpanded.value);

    const closePanel = () => {
      showContent.value = false;
      setTimeout(() => {
        isExpanded.value = false;
        // 保存状态到localStorage
        localStorage.setItem('introPanelExpanded', 'false');
      }, 150);
    };

    const expandPanel = () => {
      showPanel.value = true;
      isExpanded.value = true;
      // 保存状态到localStorage
      localStorage.setItem('introPanelExpanded', 'true');
      setTimeout(() => {
        showContent.value = true;
      }, 200);
    };

    return {
      showPanel,
      isExpanded,
      showContent,
      closePanel,
      expandPanel
    };
  }
};
</script>

<style scoped>
/* 介绍面板样式 */
.intro-panel {
  position: absolute;
  top: 15%;
  left: 10px;
  width: 340px;
  height: 420px;
  background: rgba(250, 250, 250, 0.74);
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(143, 143, 143, 0.3);
  z-index: 1100;
  padding: 10px;
  box-sizing: border-box;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-in-out;
}

/* 展开状态样式 */
.intro-panel.expanded {
  width: 340px;
  height: 420px;
  left: 10px;
}

/* 收缩状态样式 */
.intro-panel:not(.expanded) {
  width: 40px;
  height: 40px;
  left: 0px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  border-radius: 50%;
  background: rgba(250, 250, 250, 0.74);
  backdrop-filter: blur(6px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.intro-close-button {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: rgba(92, 91, 91, 0.2);
  border: none;
  border-radius: 50%;
  font-size: 16px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.intro-close-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.intro-content {
  flex: 1;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100% - 5px);
  padding-right: 8px;
  margin-top: 0px;
}

.intro-content::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.intro-content::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 2px;
  margin: 20px 2px 5px 0;
}

.intro-content::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.intro-content:hover::-webkit-scrollbar-thumb {
  background: rgba(143, 143, 143, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.intro-content:hover::-webkit-scrollbar-thumb:hover {
  background: rgba(143, 143, 143, 0.7);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

/* Vue过渡动画 */
.intro-content.v-enter-active,
.intro-content.v-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.intro-content.v-enter-from,
.intro-content.v-leave-to {
  opacity: 0;
}

/* Fade过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.intro-text {
  font-size: 12px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.95);
  text-align: left;
  margin-top: -14px;
  padding-bottom: 10px;
}

.intro-title {
  font-size: 14px;
  font-weight: 500;
  color: rgb(20, 20, 20);
  text-align: left;
  margin-bottom: 8px;
}

.title-content {
  font-size: 12px;
  line-height: 1.35;
  color: rgba(34, 34, 34, 0.95);
  text-align: left;
  font-weight: 400;
  margin-top: 0px;
}

.intro-section-title {
  font-size: 15px;
  font-weight: 600;
  color: rgb(20, 20, 20);
  margin-top: -4px;
  margin-bottom: 8px;
  text-align: left;
}

/* 展开状态内容样式 */
.expanded-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
}

/* 收缩状态内容样式 */
.collapsed-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  height: 100%;
  width: 100%;
  transition: all 0.2s ease;
  border-radius: 50%;
  position: relative;
}

.collapsed-content:hover {
  background: rgba(0, 0, 0, 0.05);
  transform: scale(1.02);
}

.collapsed-content:active {
  transform: scale(0.98);
}

.expand-icon {
  font-size: 16px;
  font-weight: 400;
  color: #666;
  margin: 0;
  opacity: 0.8;
}

.expand-text {
  display: none;
}
</style> 